import React, { useState } from 'react';
import { Button, Alert, Spinner, Card, CardHeader, CardBody, Form, FormGroup, Label, Input } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

export const AdminCandles = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Calculate dates for last 5 years
  const today = new Date();
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(today.getFullYear() - 5);

  const [interval, setInterval] = useState('Day');
  const [fromDate, setFromDate] = useState(fiveYearsAgo.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [tickerId, setTickerId] = useState('');
  const [tickerTag, setTickerTag] = useState('');
  const [source, setSource] = useState('');
  const [collectCandles, setCollectCandles] = useState(null);
  const [skipIfAnyCandleExist, setSkipIfAnyCandleExist] = useState(false);
  const [tickerTypes, setTickerTypes] = useState([]);
  const [selectedTickerTypes, setSelectedTickerTypes] = useState([]);
  const [sources, setSources] = useState([]);

  React.useEffect(() => {
    apiClient.getTickerTypes()
      .then(types => setTickerTypes(types))
      .catch(err => console.error('Failed to fetch ticker types:', err));
    apiClient.getSources()
      .then(srcs => setSources(srcs))
      .catch(err => console.error('Failed to fetch sources:', err));
  }, []);

  const handleCollect = async () => {
    if (!window.confirm(`Collect ${interval} candles from ${fromDate} to ${toDate}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const tickersFilter = {};

      if (collectCandles !== null) {
        tickersFilter.CollectCandles = collectCandles;
      }

      if (tickerId.trim()) {
        const id = parseInt(tickerId.trim(), 10);
        if (!isNaN(id)) {
          tickersFilter.Id = id;
        }
      }

      if (tickerTag.trim()) {
        tickersFilter.Tag = tickerTag.trim();
      }

      if (source) {
        tickersFilter.Source = source;
      }

      if (selectedTickerTypes.length > 0) {
        tickersFilter.Types = selectedTickerTypes;
      }

      await apiClient.collectCandles({
        Interval: interval,
        From: fromDate,
        To: toDate,
        TickersFilter: tickersFilter,
        SkipIfAnyCandleExist: skipIfAnyCandleExist
      });

      setSuccess('Candle collection initiated successfully! The operation has been queued for processing.');
    } catch (exc) {
      console.error('Failed to collect candles:', exc);
      setError(exc.message || 'Failed to collect candles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Candles Management</h2>
      </div>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      <Card>
        <CardHeader>
          <h5 className="mb-0">Collect Candles</h5>
        </CardHeader>
        <CardBody>
          <Form>
            <FormGroup>
              <Label for="interval">Interval</Label>
              <Input
                type="select"
                id="interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                disabled={loading}
              >
                <option value="Minute">Minute</option>
                <option value="Day">Day</option>
                <option value="Week">Week</option>
                <option value="Month">Month</option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="fromDate">From Date</Label>
              <Input
                type="date"
                id="fromDate"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <Label for="toDate">To Date</Label>
              <Input
                type="date"
                id="toDate"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={loading}
              />
            </FormGroup>

            <Card className="mb-3">
              <CardHeader>
                <h6 className="mb-0">Tickers Filter</h6>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label for="tickerId">Ticker ID</Label>
                  <Input
                    type="text"
                    id="tickerId"
                    value={tickerId}
                    onChange={(e) => setTickerId(e.target.value)}
                    disabled={loading}
                    placeholder="Optional: filter by specific ticker ID"
                  />
                  <small className="form-text text-muted">
                    Optional: enter a specific ticker ID to collect candles for only that ticker.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="tickerTag">Ticker Tag</Label>
                  <Input
                    type="text"
                    id="tickerTag"
                    value={tickerTag}
                    onChange={(e) => setTickerTag(e.target.value)}
                    disabled={loading}
                    placeholder="Optional: filter by tag"
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="source">Source</Label>
                  <Input
                    type="select"
                    id="source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">&lt;Any&gt;</option>
                    {sources.map((src) => (
                      <option key={src} value={src}>
                        {src}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="tickerTypes">Ticker Types</Label>
                  <Input
                    type="select"
                    id="tickerTypes"
                    multiple
                    value={selectedTickerTypes}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setSelectedTickerTypes(selected);
                    }}
                    disabled={loading}
                    style={{ height: '150px' }}
                  >
                    {tickerTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Input>
                  <small className="form-text text-muted">
                    Hold Ctrl/Cmd to select multiple types
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="collectCandles">Collect Candles</Label>
                  <Input
                    type="select"
                    id="collectCandles"
                    value={collectCandles === null ? '' : collectCandles.toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setCollectCandles(null);
                      } else {
                        setCollectCandles(value === 'true');
                      }
                    }}
                    disabled={loading}
                  >
                    <option value="">All (ignore this filter)</option>
                    <option value="true">True (only tickers with CollectCandles enabled)</option>
                    <option value="false">False (only tickers with CollectCandles disabled)</option>
                  </Input>
                  <small className="form-text text-muted">
                    Filter by CollectCandles flag. Select 'All' to ignore this filter.
                  </small>
                </FormGroup>
              </CardBody>
            </Card>

            <FormGroup check className="mb-3">
              <Label check>
                <Input
                  type="checkbox"
                  checked={skipIfAnyCandleExist}
                  onChange={(e) => setSkipIfAnyCandleExist(e.target.checked)}
                  disabled={loading}
                />
                {' '}Skip If Any Candle Exists
              </Label>
            </FormGroup>

            <Button
              color="primary"
              onClick={handleCollect}
              disabled={loading}
              block
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Collecting...
                </>
              ) : (
                'Collect'
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};
